import { z } from "zod"
import { queueCountsSchema, queueWaitingSchema } from "./automation-queues"

export const SCHEDULED_QUEUE_STATUS = {
  queued: "queued",
  running: "running",
  succeeded: "succeeded",
  failed: "failed",
  interrupted: "interrupted",
  coalesced: "coalesced",
} as const
const cursorSchema = z
  .string()
  .max(300)
  .refine((value) => {
    const [time, id, extra] = value.split("|")
    return Boolean(time && z.iso.datetime({ offset: true }).safeParse(time).success && id && !extra)
  }, "Expected timestamp|record ID")
export const scheduledQueueQuerySchema = z
  .object({
    run: z.string().trim().min(1).max(200).optional(),
    job: z.string().trim().min(1).max(200).optional(),
    status: z.enum(SCHEDULED_QUEUE_STATUS).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
    before: cursorSchema.optional(),
  })
  .strict()
export type ScheduledQueueQuery = z.infer<typeof scheduledQueueQuerySchema>
const scheduledQueueRowSchema = z.object({
  id: z.string(),
  scheduledJobId: z.string(),
  effectiveStatus: z.enum(SCHEDULED_QUEUE_STATUS),
  status: z.enum(SCHEDULED_QUEUE_STATUS),
  triggeredAt: z.string(),
  startedAt: z.string().nullable(),
  failureSummary: z.string().nullable(),
  attemptCount: z.number().int().min(0).max(10),
  latestFailure: z
    .object({
      attempt: z.number().int().min(1).max(10),
      startedAt: z.string(),
      finishedAt: z.string().nullable(),
      outcome: z.enum(["failed", "interrupted"]),
      summary: z.string().max(1000).nullable(),
    })
    .nullable(),
  coalescedIntoRunId: z.string().nullable(),
  predecessorRunId: z.string().nullable(),
  continuationReason: z.string().nullable(),
  scheduleEnabled: z.boolean(),
  cursor: z.string(),
})
export const scheduledQueueSchema = z.object({
  observedAt: z.string(),
  rows: z.array(scheduledQueueRowSchema),
  counts: queueCountsSchema,
  waiting: queueWaitingSchema,
  pausedSchedules: z.number().int().nonnegative(),
  retrying: z.number().int().nonnegative(),
  nextBefore: z.string().nullable(),
})
export type ScheduledQueue = z.infer<typeof scheduledQueueSchema>
export const outboundQueueSchema = z.object({
  observedAt: z.string(),
  counts: queueCountsSchema,
  waiting: queueWaitingSchema,
  retrying: z.number().int().nonnegative(),
  expiredClaims: z.number().int().nonnegative(),
  dispatchPaused: z.boolean().nullable(),
  captureStartedAt: z.string().nullable(),
  nextRetryAt: z.string().nullable(),
})
export type OutboundQueue = z.infer<typeof outboundQueueSchema>
